/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useCallback, useEffect, useRef } from "react"
import Joyride, { CallBackProps, STATUS, EVENTS, ACTIONS } from "react-joyride"

// --- ADD NEW ICON IMPORTS ---
import CloseIcon from "@mui/icons-material/Close"
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew"
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"
import { CrossIcon, NextOutlineIcon } from "../Icons/LivisIcons"

import { useTranslation } from "react-i18next"

interface Step {
  // Standard JoyRide properties
  target?: string
  content?: string
  title?: string
  placement?: "top" | "bottom" | "left" | "right" | "center" | "auto"
  disableBeacon?: boolean
  disableOverlayClose?: boolean
  hideCloseButton?: boolean
  hideFooter?: boolean
  spotlightClicks?: boolean
  styles?: object

  // Your custom format properties
  element?: string
  intro?: string
  position?: "top" | "bottom" | "left" | "right" | "center" | "auto"
  tooltipClass?: string

  // Optional properties
  autoAdvanceOnClick?: string
}

// Export the Step interface
export type { Step }

interface JoyRideWrapperProps {
  steps: Step[]
  tourKey: string
  isOpen: boolean
  onTourEnd: () => void
  onTourStart?: () => void
  continuous?: boolean
  showProgress?: boolean
  showSkipButton?: boolean
  spotlightClicks?: boolean
  disableOverlay?: boolean
  disableScrolling?: boolean
  onStepChange?: (stepIndex: number) => void
  // NEW PROPS for controlling navigation buttons in modal/interactive tours
  hideBackSteps?: number[] // Array of step indices where back button should be hidden
  hideNextSteps?: number[] // Array of step indices where next button should be hidden
}

// --- UPDATED CustomTooltip COMPONENT with dynamic step counting and navigation control ---
const CustomTooltip = ({
  index,
  size,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
  skipProps,
  onSkipTour,
  onTourEnd,
  skippedSteps, // NEW PROP
  tourKey, // NEW PROP
  hideBackSteps, // NEW PROP
  hideNextSteps, // NEW PROP
}: any) => {
  const { t } = useTranslation()

  const handleSkipTour = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    console.log(":octagonal_sign: Skip tour clicked")
    if (onSkipTour) {
      onSkipTour()
    }
  }

  // Calculate visible step numbers
  const calculateVisibleNumbers = () => {
    const skippedSet = new Set(skippedSteps || [])

    // Count how many steps before current index are skipped
    let skippedBeforeCurrent = 0
    for (let i = 0; i < index; i++) {
      if (skippedSet.has(i)) {
        skippedBeforeCurrent++
      }
    }

    // Calculate visible index and total
    const visibleIndex = index + 1 - skippedBeforeCurrent
    const visibleTotal = size - skippedSet.size

    return { visibleIndex, visibleTotal }
  }

  const { visibleIndex, visibleTotal } = calculateVisibleNumbers()

  // Check if buttons should be hidden for modal/interactive tours
  const isModalOrInteractive = tourKey === "modal" || tourKey === "interactive"
  const shouldHideBack =
    isModalOrInteractive && hideBackSteps && hideBackSteps.includes(index)
  const shouldHideNext =
    isModalOrInteractive && hideNextSteps && hideNextSteps.includes(index)

  // Welcome modal
  if (step.tooltipClass === "custom-tooltip-welcome") {
    return (
      <div className="joyride-welcome-modal" {...tooltipProps}>
        <div className="joyride-welcome-content">
          <div className="joyride-welcome-left">
            <h2>{step.title}</h2>
            <p>{step.content}</p>
          </div>
          <div className="joyride-welcome-right">
            <img
              src="/image/help-center/livis-tour-welcome-image.png"
              alt="Dashboard Preview"
              className="joyride-welcome-right-image"
            />
          </div>
        </div>

        <button {...primaryProps} className="joyride-welcome-start-btn">
          {t("Start Tour")}
        </button>

        <button
          {...skipProps}
          className="joyride-welcome-close"
          onClick={handleSkipTour}
        >
          <CloseIcon color="primary" />
        </button>
      </div>
    )
  }

  // Regular tooltip with dynamic step counting and conditional navigation
  return (
    <div className="joyride-tooltip-regular" {...tooltipProps}>
      <button
        {...skipProps}
        className="joyride-regular-close"
        onClick={handleSkipTour}
      >
        <CloseIcon style={{ color: "white", fill: "white" }} />
      </button>

      <div className="joyride-regular-header">
        <h3>{step.title}</h3>
      </div>
      <div className="joyride-regular-body">
        <p>{step.content}</p>
      </div>

      <div className="joyride-regular-footer">
        <div className="joyride-regular-pagination">
          {!shouldHideBack && (
            <button
              {...backProps}
              disabled={index === 0}
              className="joyride-regular-arrow-back"
            >
              <ArrowBackIosNewIcon />
            </button>
          )}
          {/* UPDATED: Show visible step numbers instead of actual index */}
          {/* <span>{visibleIndex} of {visibleTotal}</span> */}
          {!shouldHideNext && (
            <button {...primaryProps} className="joyride-regular-arrow">
              <NextOutlineIcon />
            </button>
          )}
        </div>
        <div className="joyride-regular-buttons">
          {index > 0 && !shouldHideBack && (
            <button {...backProps} className="joyride-btn joyride-btn-back">
              {t("Back")}
            </button>
          )}
          {!shouldHideNext && (
            <button
              {...primaryProps}
              className="joyride-btn joyride-btn-primary"
            >
              {/* UPDATED: Check against visible total for "Finish" text */}
              {visibleIndex === visibleTotal ? t("Finish") : t("Next")}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const waitForElement = (
  selector: string,
  timeout = 10000
): Promise<Element | null> => {
  return new Promise((resolve) => {
    const element = document.querySelector(selector)
    if (element) {
      console.log(`:white_check_mark: Element found immediately: ${selector}`)
      const rect = element.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) {
        resolve(element)
        return
      }
      resolve(element)
      return
    }
    console.log(`:mag: Waiting for element: ${selector}`)
    const observer = new MutationObserver(() => {
      const element = document.querySelector(selector)
      if (element) {
        const rect = element.getBoundingClientRect()
        if (rect.width > 0 && rect.height > 0) {
          console.log(
            `:white_check_mark: Element found and visible: ${selector}`
          )
          observer.disconnect()
          resolve(element)
        } else {
          console.log(
            `:white_check_mark: Element found after waiting: ${selector}`
          )
          observer.disconnect()
          resolve(element)
        }
      }
    })
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })
    setTimeout(() => {
      console.log(`:warning: Element not found after timeout: ${selector}`)
      observer.disconnect()
      resolve(null)
    }, timeout)
  })
}

const LivisTourWrapper: React.FC<JoyRideWrapperProps> = ({
  steps,
  tourKey,
  isOpen,
  onTourEnd,
  onTourStart,
  onStepChange,
  continuous = true,
  showProgress = true,
  showSkipButton = true,
  spotlightClicks = false,
  disableOverlay = false,
  disableScrolling = false,
  hideBackSteps = [], // NEW PROP
  hideNextSteps = [], // NEW PROP
}) => {
  const [stepIndex, setStepIndex] = useState(0)
  const [isElementReady, setIsElementReady] = useState(false)
  const [navigationDirection, setNavigationDirection] = useState<
    "forward" | "backward"
  >("forward")
  const [skippedSteps, setSkippedSteps] = useState<number[]>([])
  const modalObserverRef = useRef<MutationObserver | null>(null)
  const clickListenersRef = useRef<Map<string, () => void>>(new Map())
  const skippedStepsRef = useRef<Set<number>>(new Set())
  const retryCountRef = useRef<number>(0)
  const maxRetries = 2
  const clickBlockerRef = useRef<((e: Event) => void) | null>(null)

  const isModalOrInteractive = tourKey === "modal" || tourKey === "interactive"

  const convertedSteps = steps.map((step) => {
    const joyrideStep: any = {
      target: step.target || step.element || `#step-${stepIndex}`,
      content: step.content || step.intro || "Tour step content",
      title: step.title || undefined,
      placement: (step.placement || step.position || "bottom") as any,
      disableBeacon: true,
      disableOverlayClose: step.disableOverlayClose || false,
      hideCloseButton: true,
      hideFooter: true,
      // UPDATED: Only allow spotlight clicks for modal/interactive tours
      spotlightClicks: isModalOrInteractive
        ? step.spotlightClicks || spotlightClicks
        : false,
      // FIXED: Reduced offset for modal/interactive tours to minimize gap
      offset: isModalOrInteractive ? 2 : 12,
      tooltipClass: step.tooltipClass || "",
      styles: {
        spotlight: {
          borderRadius: "8px",
          // UPDATED: Remove border and shadow for modal/interactive
          border: isModalOrInteractive ? "none" : "3px solid #4f46e5",
          boxShadow: isModalOrInteractive
            ? "none"
            : "0 0 0 4px rgba(79, 70, 229, 0.3)",
          zIndex: isModalOrInteractive ? 999998 : 99999,
        },
        ...step.styles,
      },
    }

    if (step.autoAdvanceOnClick) {
      joyrideStep.autoAdvanceOnClick = step.autoAdvanceOnClick
    }

    return joyrideStep
  })

  // Element checking with bidirectional skip support - FASTER TIMING
  useEffect(() => {
    if (!isOpen || !convertedSteps[stepIndex]) return
    const currentStep = convertedSteps[stepIndex]
    const targetSelector = currentStep.target
    console.log(
      `:dart: Checking element for step ${stepIndex}: ${targetSelector} (direction: ${navigationDirection})`
    )
    const checkElement = async () => {
      setIsElementReady(false)
      // FIXED: Much faster timing for modal/interactive tours
      const baseTimeout = isModalOrInteractive ? 500 : 800
      const timeout = retryCountRef.current > 0 ? baseTimeout / 2 : baseTimeout

      const element = await waitForElement(targetSelector, timeout)
      if (element) {
        console.log(`:white_check_mark: Element ready for step ${stepIndex}`)
        retryCountRef.current = 0
        if (isModalOrInteractive) {
          // FIXED: Much shorter delay for modal/interactive tours
          setTimeout(() => setIsElementReady(true), 100)
        } else {
          setIsElementReady(true)
        }
      } else {
        console.log(
          `:x: Element not found for step ${stepIndex}: ${targetSelector} (retry ${
            retryCountRef.current + 1
          }/${maxRetries})`
        )

        if (retryCountRef.current < maxRetries) {
          retryCountRef.current++
          console.log(`:arrows_counterclockwise: Retrying step ${stepIndex}...`)
          setTimeout(() => {
            setIsElementReady(false)
          }, 50) // FIXED: Shorter retry delay
        } else {
          console.log(
            `:fast_forward: Max retries reached, skipping step ${stepIndex} (${navigationDirection})`
          )
          skippedStepsRef.current.add(stepIndex)
          setSkippedSteps(Array.from(skippedStepsRef.current))
          retryCountRef.current = 0

          const nextIndex =
            navigationDirection === "forward" ? stepIndex + 1 : stepIndex - 1

          if (
            navigationDirection === "forward" &&
            nextIndex < convertedSteps.length
          ) {
            setTimeout(() => {
              setStepIndex(nextIndex)
              onStepChange?.(nextIndex)
            }, 100) // FIXED: Shorter skip delay
          } else if (navigationDirection === "backward" && nextIndex >= 0) {
            setTimeout(() => {
              setStepIndex(nextIndex)
              onStepChange?.(nextIndex)
            }, 100) // FIXED: Shorter skip delay
          } else {
            console.log(
              ":stop_sign: Boundary reached with element not found, ending tour"
            )
            if (skippedStepsRef.current.size > 0) {
              console.log(
                `:information_source: Tour completed. Skipped steps: ${Array.from(
                  skippedStepsRef.current
                ).join(", ")}`
              )
            }
            onTourEnd()
          }
        }
      }
    }
    checkElement()
  }, [
    stepIndex,
    isOpen,
    convertedSteps,
    tourKey,
    onStepChange,
    onTourEnd,
    navigationDirection,
    isModalOrInteractive,
  ])

  // Block outside clicks for normal tours
  useEffect(() => {
    if (!isOpen || isModalOrInteractive) return

    const blockOutsideClicks = (e: Event) => {
      const target = e.target as Element

      // Allow clicks on welcome modal and its children
      const welcomeModal = document.querySelector(".joyride-welcome-modal")
      if (welcomeModal && welcomeModal.contains(target)) {
        return
      }

      // Allow clicks on regular tooltip and its children
      const tooltip = document.querySelector(".joyride-tooltip-regular")
      if (tooltip && tooltip.contains(target)) {
        return
      }

      // Allow clicks on joyride elements
      const joyrideElements = document.querySelectorAll(
        '[class*="joyride"], [class*="react-joyride"]'
      )
      Array.from(joyrideElements).forEach((element) => {
        if (element.contains(target)) {
          return
        }
      })

      console.log(":no_entry_sign: Blocking outside click during normal tour")
      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation()
    }

    // Add event listeners with capture to intercept clicks before they reach other elements
    document.addEventListener("click", blockOutsideClicks, true)
    document.addEventListener("mousedown", blockOutsideClicks, true)
    document.addEventListener("mouseup", blockOutsideClicks, true)
    document.addEventListener("touchstart", blockOutsideClicks, true)
    document.addEventListener("touchend", blockOutsideClicks, true)

    clickBlockerRef.current = blockOutsideClicks

    return () => {
      document.removeEventListener("click", blockOutsideClicks, true)
      document.removeEventListener("mousedown", blockOutsideClicks, true)
      document.removeEventListener("mouseup", blockOutsideClicks, true)
      document.removeEventListener("touchstart", blockOutsideClicks, true)
      document.removeEventListener("touchend", blockOutsideClicks, true)
      clickBlockerRef.current = null
    }
  }, [isOpen, isModalOrInteractive])

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { action, index, status, type, origin, lifecycle } = data
      console.log(":dart: Joyride Callback:", {
        action,
        index,
        status,
        type,
        origin,
        lifecycle,
        tourKey,
      })

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        console.log(":stop_sign: Tour ending...")
        setStepIndex(0)
        if (isModalOrInteractive) {
          document.body.classList.remove(
            ...Array.from(document.body.classList).filter((cls) =>
              cls.startsWith("tour-step-")
            )
          )
        }
        onTourEnd()
        return
      }

      if (type === EVENTS.TOUR_START) {
        onTourStart?.()
      }

      if (type === EVENTS.STEP_AFTER) {
        if (origin === "overlay") {
          console.log(":no_entry_sign: Ignoring overlay click")
          return
        }

        // UPDATED: For normal tours, ignore spotlight clicks - using string comparison to avoid TS type issues
        if (!isModalOrInteractive && String(origin) === "spotlight") {
          console.log(
            ":no_entry_sign: Ignoring spotlight click for normal tour"
          )
          return
        }

        const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1

        if (action === ACTIONS.PREV) {
          setNavigationDirection("backward")
        } else {
          setNavigationDirection("forward")
        }

        setStepIndex(nextIndex)
        onStepChange?.(nextIndex)
      } else if (type === EVENTS.TARGET_NOT_FOUND) {
        console.log(
          `:warning: Target not found for step ${index}, skipping (${navigationDirection})...`
        )
        const nextIndex =
          navigationDirection === "forward" ? index + 1 : index - 1

        if (
          navigationDirection === "forward" &&
          nextIndex < convertedSteps.length
        ) {
          setStepIndex(nextIndex)
          onStepChange?.(nextIndex)
        } else if (navigationDirection === "backward" && nextIndex >= 0) {
          setStepIndex(nextIndex)
          onStepChange?.(nextIndex)
        } else {
          console.log(
            ":stop_sign: Boundary reached with target not found, ending tour"
          )
          onTourEnd()
        }
        return
      }

      if (isModalOrInteractive) {
        document.body.classList.remove(
          ...Array.from(document.body.classList).filter((cls) =>
            cls.startsWith("tour-step-")
          )
        )
        document.body.classList.add(`tour-step-${index}`)
      }
    },
    [
      onTourEnd,
      onTourStart,
      onStepChange,
      tourKey,
      convertedSteps,
      navigationDirection,
      isModalOrInteractive,
    ]
  )

  const setupClickListener = useCallback(
    (step: any, currentIndex: number) => {
      if (step.autoAdvanceOnClick && isModalOrInteractive) {
        const clickHandler = () => {
          console.log(
            `:three_button_mouse: Auto-advance click detected on: ${step.autoAdvanceOnClick}`
          )
          // FIXED: Much shorter delay for auto-advance
          setTimeout(() => {
            const nextIndex = currentIndex + 1
            if (nextIndex < convertedSteps.length) {
              setStepIndex(nextIndex)
              onStepChange?.(nextIndex)
            } else {
              onTourEnd()
            }
          }, 300) // FIXED: Reduced from 800ms to 300ms
        }
        setTimeout(async () => {
          const element = await waitForElement(step.autoAdvanceOnClick, 1500) // FIXED: Reduced timeout
          if (element) {
            console.log(
              `:white_check_mark: Setting up auto-advance listener on: ${step.autoAdvanceOnClick}`
            )
            element.addEventListener("click", clickHandler)
            clickListenersRef.current.set(step.autoAdvanceOnClick, clickHandler)
          }
        }, 50) // FIXED: Much shorter setup delay
      }
    },
    [convertedSteps, onStepChange, onTourEnd, isModalOrInteractive]
  )

  useEffect(() => {
    if (!isOpen || !isModalOrInteractive) return
    if (stepIndex === 0) {
      const observer = new MutationObserver(() => {
        let modalSelector
        let modalElement
        if (tourKey === "modal") {
          modalSelector = "#workstation_name"
          modalElement = document.querySelector(modalSelector)
        } else if (tourKey === "interactive") {
          const interactiveSelectors = [
            "#livis-add-part-name",
            "#livis-add-ai-inspector-usecase-name",
            "#livis-workstation-name",
            ".livis-upload-part-data-wrap",
            "#livis-edit-model-name",
            "#livis-import-data-name",
          ]
          for (const selector of interactiveSelectors) {
            const element = document.querySelector(selector)
            if (element) {
              modalSelector = selector
              modalElement = element
              console.log(`:mag: Found interactive modal element: ${selector}`)
              break
            }
          }
        }

        if (modalElement && modalSelector) {
          console.log(
            `:tada: Modal detected with selector: ${modalSelector}! Auto-advancing to step 2`
          )
          setTimeout(() => {
            setStepIndex(1)
            onStepChange?.(1)
          }, 200) // FIXED: Reduced from 500ms to 200ms
        }
      })
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      })
      modalObserverRef.current = observer
      return () => {
        observer.disconnect()
      }
    } else {
      const currentStep = convertedSteps[stepIndex]
      if (currentStep) {
        setupClickListener(currentStep, stepIndex)
      }
    }
  }, [
    isOpen,
    tourKey,
    stepIndex,
    onStepChange,
    convertedSteps,
    setupClickListener,
    isModalOrInteractive,
  ])

  useEffect(() => {
    if (!isOpen) {
      setStepIndex(0)
      setIsElementReady(false)
      setNavigationDirection("forward")
      retryCountRef.current = 0
      skippedStepsRef.current.clear()
      setSkippedSteps([])
      if (isModalOrInteractive) {
        document.body.classList.remove(
          ...Array.from(document.body.classList).filter((cls) =>
            cls.startsWith("tour-step-")
          )
        )
      }
      clickListenersRef.current.forEach((listener, selector) => {
        const element = document.querySelector(selector)
        if (element) {
          element.removeEventListener("click", listener)
        }
      })
      clickListenersRef.current.clear()
      if (modalObserverRef.current) {
        modalObserverRef.current.disconnect()
      }
      // Clean up click blocker
      if (clickBlockerRef.current) {
        document.removeEventListener("click", clickBlockerRef.current, true)
        document.removeEventListener("mousedown", clickBlockerRef.current, true)
        document.removeEventListener("mouseup", clickBlockerRef.current, true)
        document.removeEventListener(
          "touchstart",
          clickBlockerRef.current,
          true
        )
        document.removeEventListener("touchend", clickBlockerRef.current, true)
        clickBlockerRef.current = null
      }
    }
  }, [isOpen, tourKey, isModalOrInteractive])

  return (
    <>
      <style>{`
        :root {
          --tour-blue: #1e58d5;
          --tour-blue-dark: #1c4ed8;
          --tour-blue-light: #3b82f6;
          --tour-text-light: #f1f5f9;
          --tour-text-dark: #334155;
        }

        .__floater.__floater__open{
          filter: none !important;
        }

        .react-joyride__tooltip {
          background-color: transparent !important;
          box-shadow: none !important;
          filter: none !important;
          -webkit-filter: none !important;
          border: none !important;
          padding: 0 !important;
        }
        
        .react-joyride__tooltip [data-testid="arrow"] {
          transform: rotate(45deg);
          background: var(--livis-color-primary-blue) !important;
          width: 16px !important;
          height: 16px !important;
          position: absolute !important;
        }
        .react-joyride__tooltip[data-placement*="bottom"] [data-testid="arrow"] {
          top: -8px !important;
          margin-left: -8px !important;
        }
        .react-joyride__tooltip[data-placement*="top"] [data-testid="arrow"] {
          bottom: -8px !important;
          margin-left: -8px !important;
        }
        .react-joyride__tooltip[data-placement*="right"] [data-testid="arrow"] {
          left: -8px !important;
          margin-top: -8px !important;
        }
        .react-joyride__tooltip[data-placement*="left"] [data-testid="arrow"] {
          right: -8px !important;
          margin-top: -8px !important;
        }

        .joyride-welcome-modal {
          background: var(--livis-white-color);
          width: 90vw;
          max-width: 950px;
          border-radius: 4px;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
          position: relative;
          overflow: hidden;
        }

        .joyride-welcome-close {
          position: absolute;
          top: 20px;
          right: 20px;
          background: var(--livis-usecase-horizontal-line-color);
          border: none;
          border-radius: 50%;
          width: 36px;
          height: 36px;
          color: #64748b;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s ease;
          z-index: 20;
        }
        // .joyride-welcome-close:hover {
        //   background: #e2e8f0;
        // }
          .joyride-welcome-close svg {

          color: var(--livis-black-font-color) !important;
          fill: var(--livis-black-font-color) !important;

          }

          .joyride-welcome-close svg path {

          color: var(--livis-black-font-color) !important;
          fill: var(--livis-black-font-color) !important;

          }

        .joyride-welcome-content {
          display: grid;
          grid-template-columns: 40% 60%;
          height: 480px;
        }

        .joyride-welcome-left {
          background: var(--livis-color-primary-blue);
          padding: 40px;
          color: white;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .joyride-welcome-left h2 {
          font-size: 2rem;
          font-weight: 700;
          margin: 0 0 16px 0;
        }
        .joyride-welcome-left p {
          font-size: 0.95rem;
          line-height: 1.6;
          opacity: 0.9;
          margin: 0;
        }

        .joyride-welcome-right {
          background: #f4f7fa;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          overflow: hidden;
          position: relative;
        }
        .joyride-welcome-right img.joyride-welcome-right-image {
          width: 125%;
          height: auto;
          border-radius: 8px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          object-fit: cover;
        }
        
        .joyride-welcome-start-btn {
          position: absolute;
          bottom: 20px;
          right: 40px;
          z-index: 10;
          background: var(--livis-color-primary-blue);
          color: #ffffff;
          border: none;
          border-radius: 20px;
          padding: 10px 24px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .joyride-tooltip-regular {
          background: var(--livis-color-primary-blue);
          color: white;
          border-radius: 4px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
          width: 320px;
          padding: 20px;
          position: relative;
        }

        .joyride-regular-close {
          position: absolute;
          top: 15px;
          right: 15px;
          background: none;
          border: none;
          cursor: pointer;
          opacity: 1;
          transition: opacity 0.2s ease;
        }
        .joyride-regular-close:hover {
          opacity: 1;
        }
        .joyride-regular-close svg {
          width: 20px;
          height: 20px;
          color: white !important;
          fill: white !important;
        }
        .joyride-regular-close svg path {
          fill: white !important;
          stroke: white !important;
        }

        .joyride-regular-header h3 {
          font-size: 1.25rem;
          font-weight: 700;
          margin: 0 0 10px 0;
          padding-right: 25px;
        }
        
        .joyride-regular-body p {
          font-size: 0.9rem;
          line-height: 1.5;
          opacity: 0.9;
          margin: 0;
        }

        .joyride-regular-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 20px;
          padding-top: 15px;
          border-top: 1px solid rgba(255, 255, 255, 0.2);
        }

        .joyride-regular-pagination {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 0.9rem;
          font-weight: 500;
        }
        
        .joyride-regular-arrow {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          opacity: 1;
          transition: opacity 0.2s ease;
          padding: 5px;
          display: inline-flex;
          align-items: center;
        }
        .joyride-regular-arrow:hover {
          opacity: 1;
        }
        .joyride-regular-arrow:disabled {
          opacity: 0.4;
          cursor: default;
        }
        .joyride-regular-arrow svg {
          font-size: 1rem;
          color: white !important;
        }
        .joyride-regular-arrow svg path {
          stroke: white !important;
        }

        .joyride-regular-arrow-back {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          opacity: 1;
          transition: opacity 0.2s ease;
          padding: 5px;
          display: inline-flex;
          align-items: center;
        }
        .joyride-regular-arrow-back:hover {
          opacity: 1;
        }
        .joyride-regular-arrow-back:disabled {
          opacity: 0.4;
          cursor: default;
        }
        .joyride-regular-arrow-back svg {
          font-size: 1rem;
          color: white !important;
          fill: white !important;
        }
        .joyride-regular-arrow-back svg path {
          fill: white !important;
          stroke: white !important;
        }

        .joyride-regular-buttons {
          display: flex;
          gap: 8px;
        }

        .joyride-btn {
          border: none;
          border-radius: 18px;
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 600;
          padding: 8px 16px;
          transition: all 0.2s ease;
        }
        
        .joyride-btn-back {
          background: none;
          color: white;
        }
        .joyride-btn-back:hover {
          background: rgba(255, 255, 255, 0.1);
        }
        
        .joyride-btn-primary {
          background: white;
          color: var(--livis-color-primary-blue);
        }
        .joyride-btn-primary:hover {
          background: var(--tour-text-light);
        }

        .react-joyride__overlay {
          /* UPDATED: Remove background for modal/interactive tours */
          background-color: ${
            isModalOrInteractive ? "transparent" : "rgba(0, 0, 0, 0.1)"
          } !important;
          z-index: ${isModalOrInteractive ? "999997" : "99998"} !important;
          /* UPDATED: For normal tours, make overlay non-clickable but keep it visible */
          pointer-events: ${isModalOrInteractive ? "auto" : "none"} !important;
        }

        .react-joyride__spotlight {
          border-radius: 8px !important;
          /* UPDATED: Remove border and shadow for modal/interactive tours */
          border: ${
            isModalOrInteractive ? "none" : "1px solid #4f46e5"
          } !important;
          box-shadow: ${
            isModalOrInteractive ? "none" : "0 0 0 1px rgba(79, 70, 229, 0.2)"
          } !important;
          z-index: ${isModalOrInteractive ? 999998 : 99999} !important;
          /* UPDATED: For normal tours, disable pointer events on spotlight to prevent clicks */
          pointer-events: ${isModalOrInteractive ? "auto" : "none"} !important;
        }

        ${
          isModalOrInteractive
            ? `
          .MuiDialog-root, .MuiModal-root { z-index: 900000 !important; }
          .MuiBackdrop-root, .MuiDialog-backdrop { z-index: 899999 !important; }
          .MuiDialog-paper, .MuiDialog-container { z-index: 900001 !important; position: relative !important; }
          .MuiDialog-paper .MuiButton-root, .MuiDialog-paper button { position: relative !important; z-index: 900002 !important; }
          .MuiDialog-paper * { pointer-events: auto !important; }
          #livis-stepper-next-btn, #livis-stepper-back-btn, #livis-stepper-submit-btn {
            position: relative !important;
            z-index: 900003 !important;
            pointer-events: auto !important;
          }
        `
            : ""
        }
      `}</style>

      <Joyride
        callback={handleJoyrideCallback}
        continuous={continuous}
        hideCloseButton={true}
        run={isOpen && isElementReady}
        scrollToFirstStep={!isModalOrInteractive}
        showProgress={false}
        showSkipButton={false}
        // UPDATED: Only enable spotlight clicks for modal/interactive tours
        spotlightClicks={isModalOrInteractive}
        disableOverlayClose={true}
        steps={convertedSteps}
        stepIndex={stepIndex}
        disableOverlay={isModalOrInteractive} // UPDATED: Disable overlay for modal/interactive
        disableScrolling={isModalOrInteractive ? true : disableScrolling}
        tooltipComponent={(props) => (
          <CustomTooltip
            {...props}
            onSkipTour={onTourEnd}
            skippedSteps={skippedSteps}
            tourKey={tourKey} // PASS tourKey
            hideBackSteps={hideBackSteps} // PASS hideBackSteps
            hideNextSteps={hideNextSteps} // PASS hideNextSteps
          />
        )}
        scrollOffset={isModalOrInteractive ? 0 : 100}
        scrollDuration={isModalOrInteractive ? 0 : 300}
        styles={{
          options: {
            primaryColor: "#4f46e5",
            textColor: "#1f2937",
            backgroundColor: "#ffffff",
            // UPDATED: Remove overlay color for modal/interactive
            overlayColor: isModalOrInteractive
              ? "transparent"
              : "rgba(0, 0, 0, 0.1)",
            // UPDATED: Remove spotlight shadow for modal/interactive
            spotlightShadow: isModalOrInteractive
              ? "none"
              : "0 0 0 1px rgba(79, 70, 229, 0.4)",
            beaconSize: 36,
            zIndex: isModalOrInteractive ? 999997 : 99999,
            arrowColor: "var(--livis-color-primary-blue)",
            width: isModalOrInteractive ? 350 : 380,
          },
          tooltip: {
            borderRadius: 12,
            textAlign: "left",
            filter: "none",
            WebkitFilter: "none",
            zIndex: isModalOrInteractive ? 999999 : 100000,
          },
          spotlight: {
            borderRadius: 8,
            // UPDATED: Remove border and shadow for modal/interactive
            border: isModalOrInteractive ? "none" : "1px solid #4f46e5",
            boxShadow: isModalOrInteractive
              ? "none"
              : "0 0 0 1px rgba(79, 70, 229, 0.2)",
            zIndex: isModalOrInteractive ? 999998 : 99999,
            // UPDATED: Disable pointer events for normal tours
            pointerEvents: isModalOrInteractive ? "auto" : "none",
          },
          overlay: {
            // UPDATED: Make overlay transparent for modal/interactive
            backgroundColor: isModalOrInteractive
              ? "transparent"
              : "rgba(0, 0, 0, 0.1)",
            zIndex: isModalOrInteractive ? 999997 : 99998,
            // UPDATED: Disable pointer events on overlay for normal tours to prevent accidental clicks
            pointerEvents: isModalOrInteractive ? "auto" : "none",
          },
        }}
      />
    </>
  )
}

export default LivisTourWrapper
